import { API } from 'aws-amplify';
import { Buffer } from 'buffer';

const AUTH_CONFIG = {
    domain: process.env.REACT_APP_SSO_COGNITO_BASE_URL,
    clientId: process.env.REACT_APP_WEBCLIENT_ID as string,
    redirectUri: `${window.location.protocol}//${window.location.host}/sso/callback`,
    logoutUri: `${window.location.protocol}//${window.location.host}/sso/logout/callback`,
    provisioningDomain: process.env.REACT_APP_SSO_PROVISIONING_URL as string,
};

export interface TokenResponse {
    message: string;
    id_token: string;
}

export interface Session {
    authenticated: boolean;
    user?: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        contactId: string;
    };
}

interface DecodedIdToken {
    sub: string;
    'custom:account_provisioned': string;
    [key: string]: any;
}

// Held in memory only; the actual tokens live in HttpOnly cookies
let idToken: string | null = null;

export const getAuthToken = (): string | null => idToken;

export const getAuthHeaders = (): { Authorization: string } | Record<string, never> => {
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
};

// JWT decoding function
const decodeJwt = (token: string): DecodedIdToken => {
    try {
        const base64Url = token.split('.')[1]; // Get payload part
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''),
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        throw new Error('Invalid token format');
    }
};

// Generate a random string for code verifier
const generateRandomString = (length: number): string => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

// Generate a random code verifier
const generateCodeVerifier = (): string => {
    return generateRandomString(32);
};

// Generate a random state
const generateState = (): string => {
    return generateRandomString(16);
};

// Generate code challenge from verifier
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64Url = Buffer.from(digest).toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    return base64Url;
};

// Store PKCE and state values in session storage
const storePKCEValues = (codeVerifier: string, state: string): void => {
    sessionStorage.setItem('code_verifier', codeVerifier);
    sessionStorage.setItem('state', state);
};

// Get stored PKCE values
const getPKCEValues = (): { codeVerifier: string; state: string } => {
    const codeVerifier = sessionStorage.getItem('code_verifier') || '';
    const state = sessionStorage.getItem('state') || '';
    return { codeVerifier, state };
};

// Clear stored PKCE values
const clearPKCEValues = (): void => {
    sessionStorage.removeItem('code_verifier');
    sessionStorage.removeItem('state');
};

export const auth = {
    // Initiate login flow
    login: async (): Promise<void> => {
        const codeVerifier = generateCodeVerifier();
        const state = generateState();
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        storePKCEValues(codeVerifier, state);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: AUTH_CONFIG.clientId,
            redirect_uri: AUTH_CONFIG.redirectUri,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state: state,
        });

        window.location.href = `${AUTH_CONFIG.domain}/oauth2/authorize?${params.toString()}`;
    },

    signup: async (): Promise<void> => {
        const codeVerifier = generateCodeVerifier();
        const state = generateState();
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        storePKCEValues(codeVerifier, state);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: AUTH_CONFIG.clientId,
            redirect_uri: AUTH_CONFIG.redirectUri,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state: state,
        });

        window.location.href = `${AUTH_CONFIG.domain}/signup?${params.toString()}`;
    },

    signout: async (): Promise<void> => {
        const params = new URLSearchParams({
            client_id: AUTH_CONFIG.clientId,
            logout_uri: AUTH_CONFIG.logoutUri,
        });

        window.location.href = `${AUTH_CONFIG.domain}/logout?${params.toString()}`;
    },

    revokeTokenAndClearCookies: async (): Promise<void> => {
        await API.post('membership', '/auth/logout', {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        });
        idToken = null;
    },

    exchangeToken: async (code: string, receivedState: string): Promise<TokenResponse> => {
        const { codeVerifier, state } = getPKCEValues();

        if (state !== receivedState) {
            throw new Error('Invalid state parameter');
        }

        const tokens = await API.post('membership', '/auth/token', {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
            body: {
                code,
                code_verifier: codeVerifier,
                state: receivedState,
            }
        });
        
        clearPKCEValues();
        idToken = tokens.id_token;

        // Check if account is provisioned
        const decodedToken = decodeJwt(tokens.id_token);
        const isProvisioned = decodedToken['custom:account_provisioned'] === 'true';

        if (!isProvisioned) {
            // Redirect to provisioning page
            const provisioningUrl = new URL(`${AUTH_CONFIG.provisioningDomain}`);
            provisioningUrl.searchParams.append('client_id', AUTH_CONFIG.clientId);
            provisioningUrl.searchParams.append('sub', decodedToken.sub);
            provisioningUrl.searchParams.append('redirect_uri', AUTH_CONFIG.redirectUri);

            window.location.href = provisioningUrl.toString();
            return tokens;
        }

        return tokens;
    },

    refreshTokens: async (): Promise<TokenResponse> => {
        const tokens = await API.post('membership', '/auth/refresh', {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
            body: {},
        });
        idToken = tokens.id_token;
        return tokens;
    },

    getSession: async (): Promise<Session> => {
        return await API.get('membership', '/auth/session', {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
        });
    },
};
